import type { ComponentProps } from 'react';
import { StyleSheet, type UnistylesVariants } from 'react-native-unistyles';
import Text from '@/components/UI/Text';
import { graphql } from '@/gql/graphql';
import UnistylesButton from '../Unistyles/Button';
import Icon from '../Unistyles/Icon';

type UnistyleProps = UnistylesVariants<typeof style>;

type RsvpStatusValue =
  | ReturnType<typeof graphql.scalar<'rsvp_status_enum'>>
  | 'mixed'
  | null;

export type RsvpPickerButton = {
  name: string;
  rsvp: RsvpStatusValue;
  icon: ComponentProps<typeof Icon>['name'];
  variant: ComponentProps<typeof Button>['variant'];
  inactiveIconVariant?: ComponentProps<typeof Icon>['variant'];
  iconColor?: string;
  outlineColor?: string;
};

const buttonMap: Record<string, RsvpPickerButton> = {
  none: {
    name: 'none',
    rsvp: null,
    icon: 'radio-button-off',
    variant: 'secondary',
    inactiveIconVariant: 'secondary',
  },
  mixed: {
    name: 'mixed',
    rsvp: 'mixed',
    icon: 'ellipsis-horizontal',
    variant: 'secondary',
    inactiveIconVariant: 'secondary',
  },
  no: {
    name: 'no',
    rsvp: 'no',
    icon: 'close',
    variant: 'error',
    inactiveIconVariant: 'error',
  },
  maybe: {
    name: 'maybe',
    rsvp: 'maybe',
    icon: 'help',
    variant: 'warning',
    inactiveIconVariant: 'warning',
  },
  yes: {
    name: 'yes',
    rsvp: 'yes',
    icon: 'checkmark',
    variant: 'success',
    inactiveIconVariant: 'success',
  },
};

export function Button({
  style: extraStyles,
  rsvp,
  variant,
  size,
  isSelected,
  outline,
  disabled,
  forceOpacity,
  count,
  ...props
}: ComponentProps<typeof UnistylesButton> & {
  rsvp: RsvpStatusValue;
  isSelected?: boolean;
  forceOpacity?: boolean;
  count?: number;
} & UnistyleProps) {
  style.useVariants({
    forceOpacity,
    size,
  });

  return (
    <UnistylesButton
      iconOnly
      shape="circle"
      disabled={disabled}
      outline={outline}
      variant={buttonMap[rsvp ?? 'none'].variant}
      style={[style.button, extraStyles]}
      {...props}
    >
      {count !== undefined ? (
        <Text variant={isSelected ? 'white' : 'typographySecondary'} size="xl">
          {count}
        </Text>
      ) : (
        <Icon
          variant={
            isSelected ? 'white' : buttonMap[rsvp ?? 'none'].inactiveIconVariant
          }
          name={buttonMap[rsvp ?? 'none'].icon}
          size="2xl"
        />
      )}
    </UnistylesButton>
  );
}

export const UpsertRsvp = graphql(
  /* GraphQL */ `
    mutation UpsertRsvp(
      $eventId: uuid!
      $status: rsvp_status_enum
      $personId: String
    ) {
      insert_rsvp_one(
        object: { event_id: $eventId, status: $status, person_id: $personId }
        on_conflict: { constraint: rsvp_pkey, update_columns: [status] }
      ) {
        event_id
        person_id
        status
        notes
        event {
          id
          event_teams(
            where: {
              team: {
                team_people: {
                  person_id: { _eq: $personId }
                  member_type: { _eq: participant }
                }
              }
            }
          ) {
            event_id
            team_id
            rsvp_summary {
              event_id
              team_id
              yes
              maybe
              no
            }
          }
        }
      }
    }
  `,
);

const style = StyleSheet.create((theme) => ({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    position: 'relative',
    height: theme.size(12),
    overflow: 'hidden',
    gap: theme.gap(1),
    variants: { size: {} },
  },
  button: {
    variants: {
      size: {
        sm: {
          width: theme.size(8),
          height: theme.size(8),
        },
        md: {
          width: theme.size(10),
          height: theme.size(10),
        },
        lg: {
          width: theme.size(12),
          height: theme.size(12),
        },
        default: {
          width: theme.size(10),
          height: theme.size(10),
        },
      },
      forceOpacity: {
        true: {
          opacity: 1,
        },
      },
    },
    alignItems: 'center',
    justifyContent: 'center',
  },
  icon: {
    color: 'blue',
  },
}));
